'use client';

import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  ModelSearchSelect,
  FilterOption,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  SearchInputIc,
  ResetFiltersIc,
  AccountIcon,
  StatusFilterOrdersIc,
  PublicLinkIc,
} from '../../../../components/icons';
import {
  getSortingOptions,
  getStatusFilterOptions,
  getCampaignTypeFilterOptions,
} from '../constants';
import { FiltersProps } from '../types';
import { CustomHeroButton } from '../../../../components';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  localFansFrom,
  localRevenueFrom,
  availableModels,
  isLoading,
  hasActiveFilters,
  onSearchChange,
  onFansFromChange,
  onRevenueFromChange,
  onFilterChange,
  onResetFilters,
  onPublicLinksClick,
  onModelSearchChange,
}) => {
  const { t } = useTranslations();

  const modelOptions: FilterOption[] = availableModels.map((model) => ({
    key: model.id,
    avatar: model.avatar,
    username: model.username,
    name: model.name,
    label: `${model.name} (@${model.username})`,
  }));

  return (
    <div className="max-md:mb-6 md:pb-6 flex items-center gap-4 md:flex-wrap md:w-full max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
      {/* Search */}
      <Input
        type="text"
        placeholder={t('partner.partnerCampaigns.searchPlaceholder')}
        size="sm"
        value={localSearch}
        onChange={(e) => onSearchChange(e.target.value)}
        endContent={<SearchInputIc />}
        classNames={{
          mainWrapper: 'w-[17.0625rem]',
          base: 'w-[17.0625rem]',
          input: 'text-sm text-auth-ink placeholder:text-neutral-700',
          inputWrapper:
            'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
          innerWrapper: 'bg-transparent',
        }}
      />

      {/* Сортировка */}
      <FilterSelect
        placeholder={t('partner.partnerCampaigns.filters.sorting')}
        options={getSortingOptions(t)}
        selectedKey={filters.sortBy}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('sortBy', value)}
        startContent={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="none"
              stroke="#A8A8B0"
              strokeLinecap="round"
              strokeWidth="1.5"
              d="M22 7H2m17 5H5m11 5H8"
            />
          </svg>
        }
        autoWidth={true}
        popoverWidth={230}
      />

      {/* Аккаунт */}
      <ModelSearchSelect
        placeholder={t('partner.partnerCampaigns.filters.account')}
        options={modelOptions}
        selectedKey={filters.account}
        onSelectionChange={(value) => onFilterChange('account', value === 'all' ? '' : value)}
        onSearchChange={onModelSearchChange || (() => {})}
        isLoading={isLoading}
        startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
        variant="campaign"
        autoWidth={true}
        t={t}
      />

      {/* Тип кампании */}
      <FilterSelect
        placeholder={t('partner.partnerCampaigns.filters.campaignType') || 'Тип'}
        options={getCampaignTypeFilterOptions(t)}
        selectedKey={filters.campaignType}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('campaignType', value === 'all' ? '' : value)}
        startContent={
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.66602 10.0013C1.66602 6.07293 1.66602 4.10875 2.8864 2.88836C4.10679 1.66797 6.07098 1.66797 9.99935 1.66797C13.9277 1.66797 15.8919 1.66797 17.1123 2.88836C18.3327 4.10875 18.3327 6.07293 18.3327 10.0013C18.3327 13.9297 18.3327 15.8939 17.1123 17.1142C15.8919 18.3346 13.9277 18.3346 9.99935 18.3346C6.07098 18.3346 4.10679 18.3346 2.8864 17.1142C1.66602 15.8939 1.66602 13.9297 1.66602 10.0013Z"
              stroke="#A8A8B0"
            />
            <path d="M5.83398 15V7.5" stroke="#A8A8B0" strokeLinecap="round" />
            <path d="M10 15V5" stroke="#A8A8B0" strokeLinecap="round" />
            <path d="M14.166 14.9987V10.832" stroke="#A8A8B0" strokeLinecap="round" />
          </svg>
        }
        autoWidth={true}
      />
      {/* Статус */}
      <FilterSelect
        placeholder={t('partner.partnerCampaigns.filters.status')}
        options={getStatusFilterOptions(t)}
        selectedKey={filters.status}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('status', value === 'all' ? '' : value)}
        startContent={<StatusFilterOrdersIc size={20} />}
        autoWidth={true}
      />

      {/* Источник трафика
      <FilterSelect
        placeholder={t('partner.partnerCampaigns.filters.trafficSource')}
        options={getTrafficSourceFilterOptions(t)}
        selectedKey={filters.trafficSource}
        variant="campaign"
        onSelectionChange={(value) => onFilterChange('trafficSource', value)}
        startContent={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        autoWidth={true}
      />
      */}

      {/* Фанатов от
      <Input
        type="number"
        placeholder="0"
        startContent={
          <span className="text-auth-muted text-sm whitespace-nowrap mr-1">
            {t('partner.partnerCampaigns.filters.fansFrom')}:
          </span>
        }
        size="sm"
        value={localFansFrom}
        onValueChange={onFansFromChange}
        classNames={{
          mainWrapper: "w-fit max-w-[12.5rem]",
          base: "w-fit max-w-[12.5rem]",
          input: "text-sm text-auth-muted !pl-0",
          inputWrapper: "rounded-lg h-9 bg-auth-surface px-3",
          innerWrapper: "gap-0"
        }}
      />
      */}

      {/* Доход от
      <Input
        type="number"
        placeholder="0"
        startContent={
          <span className="text-auth-muted text-sm whitespace-nowrap mr-1">
            {t('partner.partnerCampaigns.filters.incomeFrom')}:
          </span>
        }
        size="sm"
        value={localRevenueFrom}
        onValueChange={onRevenueFromChange}
        classNames={{
          mainWrapper: "w-fit max-w-[12.5rem]",
          base: "w-fit max-w-[12.5rem]",
          input: "text-sm text-auth-muted !pl-0",
          inputWrapper: "rounded-lg h-9 bg-auth-surface px-3",
          innerWrapper: "gap-0"
        }}
      />
      */}
      {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
      {hasActiveFilters && (
        <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
          <Button
            //@ts-ignore
            size="xs"
            className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
            onPress={onResetFilters}
          >
            <ResetFiltersIc />
          </Button>
        </Tooltip>
      )}

      {/* Spacer */}
      <div className="flex-grow" />

      {/* Public Links Button */}
      {/* <CustomHeroButton
        variant="primary"
        onPress={onPublicLinksClick}
        className="h-9 rounded-2 px-4 gap-2"
      >
        <PublicLinkIc size={20} />
        {t('partner.revshare.campaigns.publicLinks') || 'Публичные ссылки'}
      </CustomHeroButton> */}
    </div>
  );
};

export default FiltersSection;
